"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FileText,
  Code2,
  Image as ImageIcon,
  Mic,
  Video,
  Bot,
  Sparkles,
  Layers,
} from "lucide-react";

const NODES = [
  { id: "text", label: "Text (LLMs)", icon: FileText, color: "#3b82f6", x: 70, y: 45 },
  { id: "code", label: "Technisch & Code", icon: Code2, color: "#8b5cf6", x: 210, y: 35 },
  { id: "image", label: "Bild & Vision", icon: ImageIcon, color: "#f59e0b", x: 350, y: 55 },
  { id: "video", label: "Video & Motion", icon: Video, color: "#f43f5e", x: 340, y: 135 },
  { id: "audio", label: "Audio & Voice", icon: Mic, color: "#10b981", x: 200, y: 145 },
  { id: "agents", label: "Agenten & Tools", icon: Bot, color: "#06b6d4", x: 80, y: 125 },
];

const CENTER = { x: 210, y: 90 };

export default function AiLandscapePreview() {
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % NODES.length);
    }, 2000);
    return () => clearInterval(timer);
  }, []);

  const activeNode = NODES[activeIdx];

  return (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-xl p-3 select-none">
      {/* Dynamic ambient radial gradient glow */}
      <div
        className="pointer-events-none absolute inset-0 opacity-25 transition-all duration-700 dark:opacity-40"
        style={{
          background: `radial-gradient(ellipse 60% 50% at 50% 50%, ${activeNode.color}25, transparent 70%)`,
        }}
      />

      <svg
        viewBox="0 0 420 180"
        className="h-full w-full max-h-[160px] max-w-[420px]"
        aria-hidden="true"
      >
        <defs>
          <filter id="ai-glow-preview" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="3.5" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* Orbit ring around center */}
        <ellipse
          cx={CENTER.x}
          cy={CENTER.y}
          rx="145"
          ry="62"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          strokeDasharray="4 6"
          className="text-zinc-200 opacity-60 dark:text-zinc-800"
        />

        {/* Connector lines between center and all modality nodes */}
        {NODES.map((node, idx) => {
          const isActive = idx === activeIdx;
          return (
            <g key={node.id}>
              {/* Connector line */}
              <line
                x1={CENTER.x}
                y1={CENTER.y}
                x2={node.x}
                y2={node.y}
                stroke={isActive ? node.color : "currentColor"}
                strokeWidth={isActive ? 2 : 1}
                strokeDasharray={isActive ? "none" : "3 4"}
                className={
                  isActive
                    ? "opacity-90 transition-all duration-500"
                    : "text-zinc-300 opacity-30 dark:text-zinc-700"
                }
              />

              {/* Pulsing beam when active */}
              {isActive && (
                <motion.circle
                  r="4"
                  fill={node.color}
                  filter="url(#ai-glow-preview)"
                  animate={{
                    cx: [CENTER.x, node.x],
                    cy: [CENTER.y, node.y],
                    opacity: [0.9, 1, 0.3],
                  }}
                  transition={{
                    duration: 1.3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              )}
            </g>
          );
        })}

        {/* Center Node (Core) */}
        <circle
          cx={CENTER.x}
          cy={CENTER.y}
          r="22"
          className="fill-white stroke-black/10 dark:fill-zinc-900 dark:stroke-white/20"
          strokeWidth="1.5"
        />
        <circle
          cx={CENTER.x}
          cy={CENTER.y}
          r="14"
          fill={activeNode.color}
          opacity="0.25"
          className="animate-pulse"
        />
        <foreignObject
          x={CENTER.x - 10}
          y={CENTER.y - 10}
          width="20"
          height="20"
          className="pointer-events-none"
        >
          <div className="flex h-full w-full items-center justify-center">
            <Sparkles className="h-4 w-4 text-zinc-800 dark:text-zinc-200" />
          </div>
        </foreignObject>

        {/* Outer Modality Nodes */}
        {NODES.map((node, idx) => {
          const isActive = idx === activeIdx;
          const Icon = node.icon;

          return (
            <g key={node.id}>
              {/* Active aura ring */}
              {isActive && (
                <circle
                  cx={node.x}
                  cy={node.y}
                  r="24"
                  fill={node.color}
                  opacity="0.2"
                  className="transition-all duration-500"
                />
              )}

              {/* Outer pill circle */}
              <circle
                cx={node.x}
                cy={node.y}
                r="16"
                className={`transition-all duration-300 ${
                  isActive
                    ? "fill-white stroke-[2.5px] shadow-md dark:fill-zinc-900"
                    : "fill-zinc-50 stroke-[1px] stroke-black/10 dark:fill-zinc-950 dark:stroke-white/15"
                }`}
                stroke={isActive ? node.color : undefined}
              />

              {/* Icon */}
              <foreignObject
                x={node.x - 8}
                y={node.y - 8}
                width="16"
                height="16"
                className="pointer-events-none"
              >
                <div className="flex h-full w-full items-center justify-center">
                  <Icon
                    className={`h-4 w-4 transition-colors duration-300 ${
                      isActive ? "" : "text-zinc-400 dark:text-zinc-600"
                    }`}
                    style={{ color: isActive ? node.color : undefined }}
                  />
                </div>
              </foreignObject>
            </g>
          );
        })}
      </svg>

      {/* Floating active label pill at bottom */}
      <div className="pointer-events-none absolute bottom-1.5 left-1/2 -translate-x-1/2">
        <span
          className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/95 px-3.5 py-1 text-xs font-bold tracking-tight shadow-sm backdrop-blur-md transition-all duration-300 dark:border-white/10 dark:bg-zinc-900/95"
          style={{ color: activeNode.color }}
        >
          <span
            className="h-2 w-2 rounded-full animate-pulse"
            style={{ backgroundColor: activeNode.color }}
          />
          {activeNode.label}
        </span>
      </div>
    </div>
  );
}
